<script lang="ts">
	import type { CarResult } from './cars/types';
	import { browser } from '$app/environment';
	import { page } from '$app/state';
	import {
		CarEnergy,
		type CarEnergy$options,
		GetCarsStore,
		type GetEnergyPrice$result,
		GetEnergyPriceStore,
		ItineraryTrafficMode,
		ItineraryTravelMode,
	} from '$houdini';
	import CaravanToggle from '$lib/components/itinerary/options/CaravanToggle.svelte';
	import Cars from '$lib/components/itinerary/options/cars/Cars.svelte';
	import EvOptions from '$lib/components/itinerary/options/cars/ev/EvOptions.svelte';
	import DistanceSystem from '$lib/components/itinerary/options/DistanceSystem.svelte';
	import EvToggle from '$lib/components/itinerary/options/EvToggle.svelte';
	import EnergyPrice from '$lib/components/itinerary/options/motorVehicle/EnergyPrice.svelte';
	import TrafficMode from '$lib/components/itinerary/options/TrafficMode.svelte';
	import TripConstraint from '$lib/components/itinerary/options/TripConstraint.svelte';
	import { getTravelModeOptions } from '$lib/components/itinerary/tripOptions';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import SegmentControl from '$lib/components/molecules/segment-control/SegmentControl.svelte';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { capitalizeFirstLetter, formatToCamelCase } from '$lib/utils/formatString';
	import { usePersistentState } from '$lib/utils/persistentState.svelte';
	import * as m from '$paraglide/messages';

	interface Props {
		carIdFromParams?: string;
	}

	interface DefaultItineraryOptions {
		car?: CarResult[];
		energyPrice?: GetEnergyPrice$result['getEnergyPrice'];
		lastFetched?: number;
		version: number;
	}

	const STORAGE_KEY = 'default-iti-options';
	const STORAGE_VERSION = 1;
	const ONE_MONTH_IN_MS = 1000 * 60 * 60 * 24 * 30; // Fetch once a month

	const defaultItineraryOptions = {
		car: undefined,
		energyPrice: undefined,
		lastFetched: undefined,
		version: STORAGE_VERSION,
	} satisfies DefaultItineraryOptions;

	const { carIdFromParams }: Props = $props();

	const { tripOptionsStore } = getMapLayoutContext();
	const searchParams = getSearchParamsStore();
	const { userProfileStore } = page.data;
	// ? idea: we should see either to store this datas as a config or have some kind of polling to only refresh when necessary (SSE ? ws ? etc)
	const useDefaultParams = usePersistentState<DefaultItineraryOptions>(STORAGE_KEY, defaultItineraryOptions, { storageType: 'local' });

	let getCarsStore: GetCarsStore;
	let energyPriceStore: GetEnergyPriceStore;

	let selectedCar: CarResult | undefined = $state();
	let isLoading = $state(false);
	let hasError = $state<('price' | 'car')[]>([]);
	const dataSource = $state<DefaultItineraryOptions>(useDefaultParams.getCurrent());

	const isUserLoggedIn = $derived(!!page.data.userSession);
	const isEV = $derived(selectedCar?.energy === CarEnergy.ELECTRIC);
	const filteredCarsList = $derived(dataSource.car?.filter(car => isEV ? car.energy === CarEnergy.ELECTRIC : car.energy !== CarEnergy.ELECTRIC) ?? []);
	const showRegisterCar = $derived(!isUserLoggedIn || (isUserLoggedIn && !filteredCarsList.some(car => !car.isDefault)));
	const withCaravan = $derived($tripOptionsStore?.withCaravan);
	const isGetMeLoading = $derived(Boolean(isUserLoggedIn && !($userProfileStore && $userProfileStore.errors) && ($userProfileStore && $userProfileStore.fetching)));

	if (browser) {
		if ($searchParams.energyPrice) {
			$tripOptionsStore.energyPrice = $searchParams.energyPrice;
		}
		setupDefaultParams();
		fetchUserTripPreferences();
	}

	async function setupDefaultParams() {
		const currentParams = useDefaultParams.getCurrent();
		const lastFetched = currentParams.lastFetched;
		hasError = [];
		isLoading = true;

		if (
			typeof lastFetched === 'number'
			&& lastFetched > Date.now() - ONE_MONTH_IN_MS
			&& currentParams.version === STORAGE_VERSION
		) {
			setParams(currentParams.car, currentParams.energyPrice);
			isLoading = false;
			return;
		}

		const [cars, energyPrice] = await Promise.allSettled([fetchCars(), fetchEnergyPrice()]);
		isLoading = false;

		if (cars.status === 'rejected') {
			hasError.push('car');
		}

		if (energyPrice.status === 'rejected') {
			hasError.push('price');
		}

		const carsData: CarResult[] | undefined = cars.status === 'fulfilled' ? cars.value : undefined;
		const energyPriceData: GetEnergyPrice$result['getEnergyPrice'] | undefined = energyPrice.status === 'fulfilled' ? energyPrice.value : undefined;

		useDefaultParams.setCurrent({
			car: carsData,
			energyPrice: energyPriceData,
			lastFetched: hasError.length > 0 ? undefined : Date.now(),
			version: STORAGE_VERSION,
		} satisfies DefaultItineraryOptions);
		setParams(carsData, energyPriceData);
	}

	function setParams(cars?: CarResult[], energyPrice?: GetEnergyPrice$result['getEnergyPrice']) {
		dataSource.car = [...(cars ?? [])];
		dataSource.energyPrice = { ...(dataSource.energyPrice ?? {}), ...(energyPrice ?? {}) } as GetEnergyPrice$result['getEnergyPrice'];
		setCarParams(cars);
		setEnergyPriceParams(energyPrice);
	}

	async function fetchCars() {
		getCarsStore ??= new GetCarsStore();
		const results = await getCarsStore.fetch({ policy: 'NetworkOnly' });

		if (results.errors) {
			return;
		}

		return results.data?.getCars;
	}

	async function fetchEnergyPrice() {
		energyPriceStore ??= new GetEnergyPriceStore();
		const results = await energyPriceStore.fetch({ policy: 'NetworkOnly' });

		if (results.errors) {
			return;
		}

		return results.data?.getEnergyPrice;
	}

	async function fetchUserTripPreferences() {
		if (!isUserLoggedIn || !userProfileStore) {
			return;
		}

		if (!$userProfileStore?.data?.me?.tripPreferences) {
			await userProfileStore.fetch({ policy: 'NetworkOnly' });
		}

		const userTripPreferences = $userProfileStore?.data?.me.tripPreferences;
		if (userTripPreferences) {
			$tripOptionsStore.tripConstraint = userTripPreferences.tripConstraint;
			$tripOptionsStore.distanceSystem = userTripPreferences.distanceSystem;
			$tripOptionsStore.currency = userTripPreferences.currency;
		}
	}

	function setEnergyPriceParams(energyPriceData?: GetEnergyPrice$result['getEnergyPrice']) {
		if (!energyPriceData) {
			return;
		}

		if (!$tripOptionsStore.energyPrice) {
			const energyPrice = energyPriceData?.[CarEnergy.GASOLINE];
			$tripOptionsStore.energyPrice
				= energyPrice && typeof energyPrice === 'object' && 'amount' in energyPrice
					? energyPrice.amount
					: undefined;
		}
	}

	function setCarParams(cars?: CarResult[]) {
		if (!cars) {
			return;
		}

		const paramsCar = cars.find(car => car.id === carIdFromParams);
		const firstCar = cars[0];
		dataSource.car = cars;
		selectCar(paramsCar ?? firstCar);
	}

	function setEnergyPrice(
		energyPriceResult?: GetEnergyPrice$result['getEnergyPrice'],
		energy?: CarEnergy$options,
	) {
		if (!energyPriceResult) {
			return;
		}

		dataSource.energyPrice = energyPriceResult;
		const energyPrice = energyPriceResult?.[energy ?? CarEnergy.GASOLINE];
		$tripOptionsStore.energyPrice = energyPrice?.amount;
	}

	function selectCar(car: CarResult) {
		selectedCar = car;
		const model = car.name;

		$tripOptionsStore.car = {
			id: car.id,
			model,
			maker: car.maker,
			isDefault: car.isDefault,
			isEv: car.energy === 'ELECTRIC',
			energy: car.energy,
		};
		setEnergyPrice(dataSource.energyPrice, car.energy);
	}

	function switchToElectricVehicle(isElectric: boolean) {
		const newCar = dataSource.car?.find(car =>
			isElectric ? car.energy === CarEnergy.ELECTRIC : car.energy !== CarEnergy.ELECTRIC,
		);
		if (newCar) {
			selectCar(newCar);
		}
	}

	async function retryFetchCars() {
		const cars = await fetchCars();
		setCarParams(cars);
	}
</script>

<p class="sr-only">{m.itinerary__options__title()}</p>
<div class="flex flex-col gap-4 px-2 md:px-0">
	<SegmentControl
		label={m.itinerary__options__travel_mode()}
		options={getTravelModeOptions()}
		defaultSelectedOption={$tripOptionsStore.travelMode}
		hideTextOnMobile
		onSelectOption={(travelMode) => {
			track({
				action: 'mobility_type',
				payload: {
					action: 'selection',
					travel_mode: travelMode,
				},
			});
			$tripOptionsStore.travelMode = travelMode;
		}}
	/>
	{#if $tripOptionsStore.travelMode === ItineraryTravelMode.CAR || $tripOptionsStore.travelMode === ItineraryTravelMode.MOTORCYCLE}
		<TrafficMode
			checked={$tripOptionsStore.traffic === ItineraryTrafficMode.ALL}
			onChange={(checked) => {
				track({
					action: 'drive_with_traffic',
					payload: {
						action: checked ? 'on' : 'off',
					},
				});

				$tripOptionsStore.traffic = checked ? ItineraryTrafficMode.ALL : ItineraryTrafficMode.CLOSINGS;
			}}
		/>
		<TripConstraint loading={isGetMeLoading} />
	{/if}
	{#if $tripOptionsStore.travelMode === ItineraryTravelMode.CAR}
		<EvToggle
			onChange={(checked) => {
				track({
					action: 'drive_with_electric_vehicle',
					payload: {
						action: checked ? 'on' : 'off',
					},
				});
				$tripOptionsStore.isElectricVehicle = checked;
				switchToElectricVehicle(checked);
			}}
			checked={isEV}
		/>
		<Cars
			onlyEv={isEV}
			carsList={filteredCarsList}
			selectedCarId={selectedCar?.id}
			hasError={hasError.includes('car')}
			isLoading={(isLoading && !hasError.includes('car') && !filteredCarsList.length) || !filteredCarsList?.length}
			{showRegisterCar}
			onRetry={retryFetchCars}
			onSelectCar={(car) => {
				track({
					action: 'car_model',
					payload: {
						action: 'selection',
						car_name: `${formatToCamelCase(car.name)}${capitalizeFirstLetter(car.energy)}`,
					},
				});
				selectCar(car);
			}}
		/>

		{#if isEV}
			<div class="mt-4">
				<EvOptions />
			</div>
		{/if}
	{/if}
	{#if $tripOptionsStore.travelMode === ItineraryTravelMode.CAR || $tripOptionsStore.travelMode === ItineraryTravelMode.MOTORCYCLE}
		<EnergyPrice
			{isEV}
			loading={(isLoading && !hasError.includes('price')) || isGetMeLoading}
			onCurrencyChange={() =>
				selectedCar && setEnergyPrice(dataSource.energyPrice, selectedCar.energy)}
		/>
	{/if}
	{#if $tripOptionsStore.travelMode === ItineraryTravelMode.CAR}
		<CaravanToggle
			checked={withCaravan}
			onChange={(checked) => {
				track({
					action: 'drive_with_caravan',
					payload: {
						action: checked ? 'on' : 'off',
					},
				});
				$tripOptionsStore.withCaravan = checked;
			}}
		/>
	{/if}
	<DistanceSystem loading={isGetMeLoading} />
</div>
